import type { RouterContextType } from "@/api/permissions/permissions.api";
// import { CheckInOutInstructions } from "@/components/features/WorkOrders/NewWorkOrder/CheckInOutInstructions";
import { ClientLocationDetails } from "@/components/features/WorkOrders/NewWorkOrder/ClientLocationDetails";
import { ContactsCard } from "@/components/features/WorkOrders/NewWorkOrder/ContactsCard";
import { TradesSubtrades } from "@/components/features/WorkOrders/NewWorkOrder/TradesSubtrades";
import { UploadPhotos } from "@/components/features/WorkOrders/NewWorkOrder/UploadPhotos";
import { WorkOrderDetails } from "@/components/features/WorkOrders/NewWorkOrder/WorkOrderDetails";
import { Button } from "@/components/ui/Button/Button";
import {
  OWNING_ENTITY_FIELD,
  transformIntakeDraftToFormValues,
  useWorkOrderForm,
  WORK_ORDER_FIELD_LABELS,
  WORK_ORDER_FIELD_ORDER,
  type WorkOrderFormValues,
} from "@/hooks/work-order/useWorkOrderForm";
import { useGetWorkOrderIntakeDraft } from "@/hooks/work-order/useWorkOrderIntakeDrafts";
import type { TWorkOrderIntakeDraft } from "@/api/work-orders/workOrders.types";
import Loader from "@/components/ui/Loader/Loader";
import { DraftSourcePanel } from "@/components/features/WorkOrders/DraftSourcePanel/DraftSourcePanel";
import { OwningEntityPicker } from "@/components/features/Entities/OwningEntityPicker";
import { useMergedView } from "@/hooks/usePlatformView";
import { useEntityTenantStore } from "@/store/entityTenantStore";
import { useRecordTenantStore } from "@/store/recordTenantStore";
import { useImpersonationStore } from "@/store/useImpersonationStore";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { leaveRecordTenant } from "@/utils/recordTenantRoute";
import { showErrorAlert, handleApiSuccess } from "@/utils/responseHandler";
import { scrollToFirstError } from "@/utils/scrollToFirstError";
import { Box, Flex, Stack, Text, Title } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type BaseSyntheticEvent } from "react";
import type { FieldErrors, UseFormSetValue } from "react-hook-form";
import { FormProvider } from "react-hook-form";
import { z } from "zod";
import styles from "./add-work-order.module.scss";

// SERV-2682: the Needs Review tab sends a dispatcher here with ?draftId= to
// complete a work order the creation bot could not finish.
const addWorkOrderSearchSchema = z.object({
  draftId: z.string().uuid().optional().catch(undefined),
});

const CREATE_FIELD_ORDER = [OWNING_ENTITY_FIELD, ...WORK_ORDER_FIELD_ORDER];

/** SERV-3232: what a client of the previously picked entity filled in, which the new one lacks. */
const clearEntityScopedFields = (setValue: UseFormSetValue<WorkOrderFormValues>) => {
  setValue("client", "");
  setValue("brand", "");
  setValue("location", "");
  setValue("siteId", "");
  setValue("locationLabel", null);
  setValue("originalWorkOrderId", null);
  setValue("originalWorkOrderLabel", null);
  setValue("contacts", []);
};

export const Route = createFileRoute("/work-orders/add-work-order/")({
  component: AddWorkOrder,
  validateSearch: addWorkOrderSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
  onLeave: leaveRecordTenant,
});

type SubmitAction = "addAnother" | "viewList";

/**
 * SERV-2682: which branch the completion route renders for a given draft-query
 * state.
 *
 * Named and exported because the rule is subtler than it looks and regressed
 * once already. It must key on whether a draft is IN HAND, never on the query's
 * error flag: query-core reports status "error" on a failed background refetch
 * while preserving the data it already had, and this decision is re-evaluated
 * on every render. Keying on the error flag therefore unmounted a half-filled
 * form on a transient network blip, discarding everything typed. The detail
 * query goes stale after 5s and refetchOnReconnect is on, so a wifi drop was
 * enough to trigger it.
 */
export type DraftLoadState = "no-draft-requested" | "loading" | "unavailable" | "ready";

export const resolveDraftLoadState = ({
  draftId,
  isLoading,
  hasDraft,
}: {
  draftId?: string;
  isLoading: boolean;
  hasDraft: boolean;
}): DraftLoadState => {
  if (!draftId) return "no-draft-requested";
  if (isLoading) return "loading";
  if (!hasDraft) return "unavailable";
  return "ready";
};

function AddWorkOrder() {
  const { draftId } = Route.useSearch();
  // Declared before the early returns below, so the error branch can route
  // rather than reach for browser history.
  const navigate = useNavigate();
  const { data: draft, isLoading: isDraftLoading } = useGetWorkOrderIntakeDraft(draftId);

  const loadState = resolveDraftLoadState({
    draftId,
    isLoading: isDraftLoading,
    hasDraft: Boolean(draft),
  });

  // The form's defaultValues are read once on mount, so the draft has to be in
  // hand before useWorkOrderForm is called. Rendering a loader until then is
  // what makes that true, rather than resetting the form after the fact.
  if (loadState === "loading") {
    return <Loader />;
  }

  // Still needed: without it the form opened blank but titled "Complete Work
  // Order" with draftId attached to the submit, so every prefilled value was
  // silently gone. See resolveDraftLoadState for why this cannot key on the
  // query's error flag.
  if (loadState === "unavailable") {
    return (
      <Flex align="center" justify="center" h="70vh" direction="column" gap="sm">
        <Text c="red">Could not load this bot draft. It may already have been completed.</Text>
        <Button
          variant="secondary"
          onClick={() => navigate({ to: "/work-orders", search: { tab: "needsReview" } })}
        >
          Back to Work Orders
        </Button>
      </Flex>
    );
  }

  return <AddWorkOrderForm draftId={draftId} draft={draft} />;
}

function AddWorkOrderForm({ draftId, draft }: { draftId?: string; draft?: TWorkOrderIntakeDraft }) {
  const initialValues = useMemo(
    () => (draft ? transformIntakeDraftToFormValues(draft) : undefined),
    [draft],
  );

  const { methods, onSubmit, isSubmitting, handleReset, owningEntityMissing, owningEntityError } =
    useWorkOrderForm({
      initialValues,
      draftId,
    });
  const recordTenant = useRecordTenantStore((state) => state.recordTenant);
  const setRecordTenant = useRecordTenantStore((state) => state.setRecordTenant);
  const clearRecordTenant = useRecordTenantStore((state) => state.clearRecordTenant);
  const mergedView = useMergedView();
  const navigate = useNavigate();
  const { isImpersonating } = useImpersonationStore();
  const [pendingAction, setPendingAction] = useState<SubmitAction | null>(null);
  const isBusy = isSubmitting || methods.formState.isSubmitting;

  const onInvalid = (errors: FieldErrors) => {
    const errorFields = Object.keys(errors)
      .map((field) => WORK_ORDER_FIELD_LABELS[field] ?? field)
      .join(", ");
    showErrorAlert(`Please fill in the required fields: ${errorFields}`, "Validation Error");
    scrollToFirstError(errors, methods.setFocus, CREATE_FIELD_ORDER);
  };

  const handleOwningEntityChange = (entityId: string) => {
    if (entityId === recordTenant) return;
    clearEntityScopedFields(methods.setValue);
    setRecordTenant(entityId);
  };

  // The picked entity outranks the label menu's, so leaving the merged view must drop it.
  useEffect(() => {
    if (mergedView) return;
    const picked = useRecordTenantStore.getState().recordTenant;
    if (!picked) return;
    if (picked !== useEntityTenantStore.getState().entityTenant) {
      clearEntityScopedFields(methods.setValue);
    }
    clearRecordTenant();
  }, [mergedView, methods.setValue, clearRecordTenant]);

  const submitWith = (action: SubmitAction, postSubmitAction: () => void) => {
    const submit = methods.handleSubmit(
      (data) =>
        onSubmit(data, () => {
          handleApiSuccess("Work order created successfully!");
          postSubmitAction();
        }),
      onInvalid,
    );
    return (event?: BaseSyntheticEvent) => {
      setPendingAction(action);
      return submit(event);
    };
  };

  const submitAndAddAnother = submitWith("addAnother", () => {
    handleReset();
    window.scrollTo({ top: 0, behavior: "smooth" });
  });
  // SERV-2682: a dispatcher who came from Needs Review goes back there, not to
  // Active, so they can see the item they just completed leave the list.
  const backToList = () =>
    navigate({
      to: "/work-orders",
      search: draftId ? { tab: "needsReview" } : {},
    });

  const submitAndViewList = submitWith("viewList", backToList);

  const handleClose = () => {
    backToList();
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitAndViewList}>
        <section className={styles.section}>
          {/* Temporary fix to account for the impersonation banner */}
          <Flex
            justify={"space-between"}
            align={"center"}
            className={styles.header}
            style={{ paddingTop: isImpersonating ? 77 : "var(--spacing-medium)" }}
          >
            <Title order={3} className={styles.title}>
              {draftId ? "Complete Work Order" : "New Work Order"}
            </Title>
            <IconX
              size={16}
              className={styles.closeIcon}
              onClick={handleClose}
              aria-label="Close"
            />
          </Flex>
          <Box className={styles.container}>
            {draft && <DraftSourcePanel draft={draft} />}
            <OwningEntityPicker
              value={recordTenant}
              onChange={handleOwningEntityChange}
              required
              error={owningEntityError}
            />
            {/* SERV-3232: the lookups below read the picked entity, and ask for none until then. */}
            {!owningEntityMissing && (
              <Flex key={recordTenant ?? ""} gap="xl" className={styles.formColumns}>
                <Stack gap="lg" className={styles.mainColumn}>
                  <ClientLocationDetails />
                  <WorkOrderDetails />
                  <TradesSubtrades />
                  {/* SERV-1926: Commenting out for MVP */}
                  {/* <CheckInOutInstructions /> */}
                  <UploadPhotos />
                </Stack>

                <Stack gap="lg" className={styles.sideColumn}>
                  <ContactsCard />
                </Stack>
              </Flex>
            )}
          </Box>
          <Box className={styles.footer}>
            <Box className={styles.footerContent}>
              {/* SERV-2682: completing a bot draft is one-shot. "Add Another"
                  resets the form to its mount-time defaults, which for a draft
                  completion are the draft's own values, while draftId stays in
                  scope. The next submit would resend a draftId the backend has
                  already resolved and come back 409. */}
              {!draftId && (
                <Button
                  variant="secondary"
                  type="button"
                  onClick={submitAndAddAnother}
                  disabled={isBusy}
                  loading={isBusy && pendingAction === "addAnother"}
                >
                  Submit & Add Another Work Order
                </Button>
              )}
              <Button
                variant="primary"
                type="submit"
                disabled={isBusy}
                loading={isBusy && pendingAction === "viewList"}
              >
                Submit & View Work Order List
              </Button>
            </Box>
          </Box>
        </section>
      </form>
    </FormProvider>
  );
}
